import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'antd';
import moment, { Moment } from 'moment-timezone';
import { DataForm, FieldProps, FieldType, FileType } from 'components';
import { Drawers } from 'app/types/drawer';
import { Unit } from 'app/services/units/types';
import { CreateWorkerRequest, Worker } from 'app/types/worker';
import { useGetEmployersQuery } from 'app/services/workers';
import { useGetCountriesQuery } from 'app/services/sites';
import { API_DATE_FORMAT, formats } from 'utils/datetime';
import { normalizeFile } from 'utils/file';

// Employer select sentinels: the unit itself, and "add a new employer" (fields appear below the select).
export const OWN_UNIT_EMPLOYER = 0;
export const NEW_EMPLOYER = -1;

export const MAX_PHOTO_SIZE = 10 * 1024 * 1024;

const dateFields = [
  'DateOfBirth',
  'EmploymentStartDate',
] as const;

const isValidTaxNumber = async (value?: string) => !value || /^\d{12}$/.test(value);
const isValidEmail = async (value?: string) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const toApiDate = (value?: Moment) => (value ? value.format(API_DATE_FORMAT) : undefined);

export const toWorkerRequest = (values: any): CreateWorkerRequest => {
  const dates = Object.fromEntries(dateFields.map((key) => [ key, toApiDate(values[key]) ]));
  const isNewEmployer = values.WorkerEmployerId === NEW_EMPLOYER;

  return {
    WorkerEmployerId: values.WorkerEmployerId > OWN_UNIT_EMPLOYER ? values.WorkerEmployerId : null,
    NewEmployer: isNewEmployer ? { Name: values.NewEmployerName, RegistrationCode: values.NewEmployerRegistrationCode } : undefined,
    FirstName: values.FirstName,
    LastName: values.LastName,
    JobTitle: values.JobTitle,
    TaxNumber: values.TaxNumber,
    PersonalIdentityCode: values.PersonalIdentityCode,
    PhoneNumber: values.PhoneNumber,
    Email: values.Email,
    AddressData: values.AddressData,
    IsInAsbestosRegister: !!values.IsInAsbestosRegister,
    IsHealthSuitableForAsbestosWork: !!values.IsHealthSuitableForAsbestosWork,
    AdditionalInfo: values.AdditionalInfo,
    InternalInduction: values.InternalInduction,
    IsActive: !!values.IsActive,
    ...dates,
  };
};

interface WorkerFormProps {
  handleSubmit: (values: any) => Promise<void>;
  worker?: Worker;
  unit: Unit;
  meta: any;
  redirectTo?: string;
}

const WorkerForm = ({ worker, unit, handleSubmit, meta, redirectTo }: WorkerFormProps) => {
  const { t } = useTranslation();
  const [ isNewEmployer, setIsNewEmployer ] = useState(false);

  const { data: employers = [], isLoading: employersLoading } = useGetEmployersQuery(unit.Id);
  const { data: countries } = useGetCountriesQuery();

  const employerOptions = useMemo(() => [
    { Id: OWN_UNIT_EMPLOYER, Name: unit.Name },
    ...employers.map((employer) => ({ Id: employer.Id, Name: employer.Name })),
    { Id: NEW_EMPLOYER, Name: t('entities.worker.addemployer') },
  ], [ employers, unit.Name, t ]);

  if (!countries) return <Skeleton active />;

  const countrySelect = { options: countries, labelKey: 'LocalName', valueKey: 'Id' };
  const datePicker = { dateFormat: formats.DD_MM_YYYY };

  const fields: FieldProps[] = [
    { name: 'PersonalInformation', type: FieldType.DIVIDER },
    { name: 'FirstName', required: true, max: 256, group: 'name' },
    { name: 'LastName', required: true, max: 256, group: 'name' },
    { name: 'PhotoFile', type: FieldType.FILE, allowedFileType: FileType.IMAGE, maxFileSize: MAX_PHOTO_SIZE },
    { name: 'DateOfBirth', type: FieldType.DATE, datePicker, group: 'identity' },
    { name: 'PersonalIdentityCode', max: 32, group: 'identity' },
    {
      name: 'TaxNumber',
      max: 32,
      group: 'job',
      validation: { validationFunction: isValidTaxNumber, message: t('forms.worker.taxnumberinvalid') },
    },
    { name: 'JobTitle', max: 256, group: 'job' },

    { name: 'ContactInformation', type: FieldType.DIVIDER },
    { name: 'PhoneNumber', required: true, max: 64, group: 'contact' },
    {
      name: 'Email',
      required: true,
      max: 256,
      group: 'contact',
      validation: { validationFunction: isValidEmail, message: t('forms.worker.emailinvalid') },
    },
    { name: 'AddressData', type: FieldType.ADDRESS, selectProps: countrySelect, initialValue: countries[0]?.Id },

    { name: 'Employment', type: FieldType.DIVIDER },
    {
      name: 'WorkerEmployerId',
      type: FieldType.SELECT,
      required: true,
      selectProps: { options: employerOptions, labelKey: 'Name', valueKey: 'Id', allowClear: false, loading: employersLoading },
      onChange: (value: number) => setIsNewEmployer(value === NEW_EMPLOYER),
    },
    ...(isNewEmployer ? [
      { name: 'NewEmployerName', required: true, max: 512, group: 'newemployer' },
      { name: 'NewEmployerRegistrationCode', max: 64, group: 'newemployer' },
    ] : []),
    { name: 'EmploymentStartDate', type: FieldType.DATE, datePicker },

    { name: 'Qualifications', type: FieldType.DIVIDER },
    { name: 'IsInAsbestosRegister', type: FieldType.BOOLEAN },

    { name: 'Health', type: FieldType.DIVIDER },
    { name: 'IsHealthSuitableForAsbestosWork', type: FieldType.BOOLEAN },

    { name: 'Notes', type: FieldType.DIVIDER },
    { name: 'AdditionalInfo', type: FieldType.TEXTAREA, max: 2048 },
    { name: 'InternalInduction', type: FieldType.TEXTAREA, max: 2048 },
    { name: 'IsActive', type: FieldType.BOOLEAN },
  ];

  const initialValues = worker
    ? {
      ...worker,
      WorkerEmployerId: worker.WorkerEmployerId ?? OWN_UNIT_EMPLOYER,
      PhotoFile: normalizeFile(worker.PhotoFileName ?? 'Photo', worker.PhotoUrl),
      ...Object.fromEntries(dateFields.map((key) => [ key, worker[key] ? moment(worker[key]) : undefined ])),
    }
    : {
      WorkerEmployerId: OWN_UNIT_EMPLOYER,
      IsInAsbestosRegister: false,
      IsHealthSuitableForAsbestosWork: false,
      IsActive: true,
    };

  return (
    <DataForm
      id={worker ? Drawers.EDIT_WORKER : Drawers.ADD_WORKER}
      entity="worker"
      onSubmit={handleSubmit}
      fields={fields}
      meta={meta}
      initialValues={initialValues}
      redirectTo={redirectTo}
    />
  );
};

export default WorkerForm;
